/**
 * App.Render - Three.js 场景与渲染（多楼层层叠）完整版
 *
 * 包含：完整渲染模块，整合第一、二部分
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import type { Node3D, End, Room, ShaftColumn, Floor } from '../types';
import { TileType } from '../types';
import { App } from './App';

// 定义扩展的Three.js类型
declare module 'three' {
  interface OrbitControls {
    enableDamping: boolean;
    dampingFactor: number;
    target: THREE.Vector3;
    maxPolarAngle: number;
    mouseButtons: {
      LEFT: number;
      MIDDLE: number;
      RIGHT: number;
    };
    enableRotate: boolean;
    update(): void;
  }
}

// 引入OrbitControls类型
type _OrbitControls = any;

/* ─────────────── Module: Render（Three.js 场景与渲染，多楼层层叠）─────────────── */
App.Render = (function () {
  const canvas = document.getElementById('c3d') as HTMLCanvasElement;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(innerWidth, innerHeight);

  // 颜色空间：确保 BasicMaterial 颜色正确输出
  if (THREE.sRGBEncoding) {
    (renderer as any).outputEncoding = THREE.sRGBEncoding;
  }

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0b1020);
  scene.fog = new THREE.Fog(0x0b1020, 60, 150);
  let sceneFog = scene.fog;

  // 光照（3D 视图使用，平面视图会关闭）
  const hemiLight = new THREE.HemisphereLight(0x9fb8ff, 0x0a0e1c, 1.0);
  scene.add(hemiLight);

  const keyLight = new THREE.DirectionalLight(0xffffff, 1.1);
  keyLight.position.set(20, 40, 20);
  scene.add(keyLight);

  const fillLight = new THREE.DirectionalLight(0x3da9fc, 0.4);
  fillLight.position.set(-25, 15, -15);
  scene.add(fillLight);

  const allLights: THREE.Light[] = [hemiLight, keyLight, fillLight];

  const camera = new THREE.PerspectiveCamera(50, innerWidth / innerHeight, 0.1, 400);
  camera.position.set(0, 30, 32);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.target.set(0, 0, 0);
  controls.maxPolarAngle = Math.PI * 0.49;

  const cameraOrtho = new THREE.OrthographicCamera(-10, 10, 10, -10, 0.1, 1000);
  cameraOrtho.position.set(0, 60, 0);
  cameraOrtho.up.set(0, 0, -1);
  cameraOrtho.lookAt(0, 0, 0);

  const controlsOrtho = new OrbitControls(cameraOrtho, canvas);
  controlsOrtho.enableRotate = false;
  controlsOrtho.enableDamping = true;
  controlsOrtho.dampingFactor = 0.08;
  controlsOrtho.target.set(0, 0, 0);
  controlsOrtho.mouseButtons = { LEFT: (THREE as any).MOUSE.PAN, MIDDLE: (THREE as any).MOUSE.DOLLY, RIGHT: (THREE as any).MOUSE.PAN };
  controlsOrtho.enabled = false;

  function setupOrtho(): void {
    const aspect = innerWidth / innerHeight;
    const G = { W: 26, D: 18 };
    const fitH = G.D + 3;
    const fitW = G.W + 3;
    let viewH = fitH;
    if (fitW / aspect > fitH) viewH = fitW / aspect;
    viewH *= 1.04;

    cameraOrtho.left = -viewH * aspect / 2;
    cameraOrtho.right = viewH * aspect / 2;
    cameraOrtho.top = viewH / 2;
    cameraOrtho.bottom = -viewH / 2;
    cameraOrtho.updateProjectionMatrix();
  }
  setupOrtho();

  const gGrid = new THREE.Group();
  const gWalk = new THREE.Group();
  const gForbid = new THREE.Group();
  const gRoom = new THREE.Group();
  const gEnd = new THREE.Group();
  const gMark = new THREE.Group();
  const gPath = new THREE.Group();
  const gSearch = new THREE.Group();
  const gJunction = new THREE.Group();
  const gRiser = new THREE.Group();
  const gRiserTile = new THREE.Group();
  const gPreview = new THREE.Group();
  const gHover = new THREE.Group();
  const gSel = new THREE.Group();

  /** 流向动画：每条路径的曲线 + 光点（沿曲线从机房端流向末端端） */
  const pathFlows: { curve: THREE.CatmullRomCurve3; points: THREE.Points; color: number }[] = [];

  scene.add(gGrid, gForbid, gWalk, gRoom, gEnd, gMark, gPath, gJunction, gRiser, gRiserTile, gSearch, gPreview, gHover, gSel);

  function makeLabel(text: string, color: string, small = false): THREE.Sprite {
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 64;
    const x = c.getContext('2d')!;
    x.font = 'bold ' + (small ? 30 : 38) + 'px Microsoft YaHei,sans-serif';
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.fillStyle = 'rgba(0,0,0,0.55)';
    x.fillRect(0, 0, 256, 64);
    x.fillStyle = color;
    x.fillText(text, 128, 34);

    const tex = new THREE.CanvasTexture(c);
    tex.minFilter = THREE.LinearFilter;

    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
    spr.scale.set(small ? 2.6 : 4.2, small ? 0.65 : 1.05, 1);
    return spr;
  }

  function fitCamera(): void {
    const S = App.Store;
    const C = App.Config;
    const n = Math.max(1, S.floors.length);

    const midY = (n - 1) * C.FLOOR_HEIGHT / 2;
    controls.target.set(0, midY, 0);

    const dist = 26 + n * 3.4;
    camera.position.set(0, midY + dist * 0.6, dist * 0.92);
    controls.update();
  }

  function buildGrid(): void {
    gGrid.clear();
    const C = App.Config;
    const G = C.GRID;
    const S = App.Store;
    const W = G.W;
    const D = G.D;

    for (const f of S.floors) {
      if (S.planView && f !== S.f) continue;

      // 平面视图不渲染地板平面和网格（避免遮挡瓦片）
      if (S.planView) continue;

      const grid = new THREE.GridHelper(Math.max(W, D), Math.max(G.W, G.D), 0x2a3458, 0x161e36);
      grid.position.y = f.yBase + 0.001;
      gGrid.add(grid);

      const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(W, D),
        new THREE.MeshStandardMaterial({ color: 0x0d1530, roughness: 1, transparent: true, opacity: 0.92 })
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = f.yBase - 0.01;
      gGrid.add(floor);
    }
  }

  function buildTiles(): void {
    const C = App.Config;
    const G = C.GRID;
    const T = C.T;
    const S = App.Store;
    const W = App.World;

    gWalk.clear();
    gForbid.clear();
    gRoom.clear();
    gEnd.clear();
    gMark.clear();
    gRiserTile.clear();

    S.floors.forEach((f: Floor, fi: number) => {
      if (S.planView && f !== S.f) return;

      const active = f === S.f;
      const go = C.GHOST_OPACITY;

      const walkPos: { x: number; z: number }[] = [];
      const forbidPos: { x: number; z: number }[] = [];

      for (let x = 0; x < G.W; x++) {
        for (let z = 0; z < G.D; z++) {
          const t = f.type[x][z];
          if (t === T.WALK) walkPos.push({ x, z });
          else if (t === T.FORBID) forbidPos.push({ x, z });
        }
      }

      if (walkPos.length) {
        const isPlan = S.planView;
        // 平面视图：极薄 BoxGeometry + BasicMaterial 纯色
        const geo = isPlan
          ? new THREE.BoxGeometry(0.94, 0.05, 0.94)
          : new THREE.BoxGeometry(0.94, 0.12, 0.94);
        const m = isPlan
          ? new THREE.MeshBasicMaterial({ color: 0xf59e3b })
          : new THREE.MeshStandardMaterial({
              color: 0xf59e3b,
              transparent: true,
              opacity: active ? 0.32 : go * 0.75,
              emissive: 0xf59e3b,
              emissiveIntensity: active ? 0.18 : 0.05,
              roughness: 0.6,
            });
        const mesh = new THREE.InstancedMesh(geo, m, walkPos.length);
        const mx = new THREE.Matrix4();

        walkPos.forEach((p, i) => {
          const wv = W.g2wy(p.x, p.z, f.yBase);
          mx.makeTranslation(wv.x, f.yBase + 0.03, wv.z);
          mesh.setMatrixAt(i, mx);
        });
        mesh.instanceMatrix.needsUpdate = true;
        (mesh as any).frustumCulled = false;
        gWalk.add(mesh);
      }

      if (forbidPos.length) {
        const isPlan = S.planView;
        // 平面视图：极薄 BoxGeometry + BasicMaterial 纯色
        const geo = isPlan
          ? new THREE.BoxGeometry(0.94, 0.05, 0.94)
          : new THREE.BoxGeometry(0.96, 1.0, 0.96);
        const m = isPlan
          ? new THREE.MeshBasicMaterial({ color: 0x5a6080 })
          : new THREE.MeshStandardMaterial({
              color: 0x2a3458,
              transparent: true,
              opacity: active ? 0.55 : go,
              roughness: 0.95,
            });
        const mesh = new THREE.InstancedMesh(geo, m, forbidPos.length);
        const mx = new THREE.Matrix4();

        forbidPos.forEach((p, i) => {
          const wv = W.g2wy(p.x, p.z, f.yBase);
          mx.makeTranslation(wv.x, f.yBase + 0.03, wv.z);
          mesh.setMatrixAt(i, mx);
        });
        mesh.instanceMatrix.needsUpdate = true;
        (mesh as any).frustumCulled = false;
        gForbid.add(mesh);
      }

      const roomPos: { x: number; z: number }[] = [];
      for (let x = 0; x < G.W; x++) {
        for (let z = 0; z < G.D; z++) {
          if (f.type[x][z] === T.ROOM) roomPos.push({ x, z });
        }
      }

      if (roomPos.length) {
        const isPlan = S.planView;
        const geo = isPlan
          ? new THREE.BoxGeometry(0.94, 0.05, 0.94)
          : new THREE.BoxGeometry(0.96, 2.0, 0.96);
        const m = isPlan
          ? new THREE.MeshBasicMaterial({ color: 0xffffff })
          : new THREE.MeshStandardMaterial({
              color: 0xffffff,
              emissive: 0x3da9fc,
              emissiveIntensity: active ? 0.25 : 0.08,
              roughness: 0.4,
              metalness: 0.0,
              transparent: !active,
              opacity: active ? 1 : go + 0.2,
            });
        const mesh = new THREE.InstancedMesh(geo, m, roomPos.length);
        const mx = new THREE.Matrix4();

        roomPos.forEach((p, i) => {
          const wv = W.g2wy(p.x, p.z, f.yBase);
          mx.makeTranslation(wv.x, f.yBase + 0.03, wv.z);
          mesh.setMatrixAt(i, mx);

          // 机房按所属管线系统着色
          const r = f.rooms.find((rr: Room) => rr.shape.some(([dx, dz]) => rr.x + dx === p.x && rr.z + dz === p.z));
          const c = r ? (App.Routing as any).systemColorOf(r.systemId, r.color) : C.ROOM_COLOR;
          mesh.setColorAt(i, new THREE.Color(c));
        });

        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        (mesh as any).frustumCulled = false;
        gRoom.add(mesh);
      }

      if (S.planView) {
        const riserPos: { x: number; z: number }[] = [];
        for (let x = 0; x < G.W; x++) {
          for (let z = 0; z < G.D; z++) {
            if (f.type[x][z] === T.RISER) riserPos.push({ x, z });
          }
        }

        if (riserPos.length) {
          const geo = new THREE.BoxGeometry(0.9, 0.05, 0.9);
          const m = new THREE.MeshBasicMaterial({ color: 0xffffff });
          const mesh = new THREE.InstancedMesh(geo, m, riserPos.length);
          const mx = new THREE.Matrix4();

          riserPos.forEach((p, i) => {
            const wv = W.g2wy(p.x, p.z, f.yBase);
            mx.makeTranslation(wv.x, f.yBase + 0.03, wv.z);
            mesh.setMatrixAt(i, mx);

            // 竖井按所属系统着色 + 编号简称标签
            const shaft = W.shaftAtCell(p.x, p.z);
            const scol = shaft ? (App.Routing as any).systemColorOf(shaft.systemId, C.SHAFT_COLOR) : C.SHAFT_COLOR;
            mesh.setColorAt(i, new THREE.Color(scol));

            if (shaft) {
              const sabbr = (App.Routing as any).systemAbbrOf(shaft.systemId);
              const slab = makeLabel('井' + shaft.id + (sabbr ? '(' + sabbr + ')' : ''), '#cdefff', true);
              slab.position.set(wv.x, f.yBase + 0.4, wv.z);
              gMark.add(slab);
            }
          });
          if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
          mesh.instanceMatrix.needsUpdate = true;
          (mesh as any).frustumCulled = false;
          gRiserTile.add(mesh);
        }
      }

      for (const r of f.rooms) {
        const cs = W.roomCells(r);
        if (!cs.length) continue;

        const cx = cs.reduce((s, c) => s + c.x, 0) / cs.length;
        const cz = cs.reduce((s, c) => s + c.z, 0) / cs.length;
        const wv = W.g2wy(cx, cz, f.yBase);

        const abbr = (App.Routing as any).systemAbbrOf(r.systemId);
        const lab = makeLabel(r.id + (abbr ? '(' + abbr + ')' : ''), '#9fd2ff');
        lab.position.set(wv.x, f.yBase + 2.7, wv.z);
        gMark.add(lab);
      }

      f.ends.forEach((e: End) => {
        const wv = W.g2wy(e.x, e.z, f.yBase);

        // 末端按所属管线系统着色
        const ecol = (App.Routing as any).systemColorOf(e.systemId, e.color);
        const isPlan = S.planView;
        const endGeo = isPlan
          ? new THREE.BoxGeometry(0.7, 0.05, 0.7)
          : new THREE.CylinderGeometry(0.34, 0.34, 1.6, 16);
        const endMat = isPlan
          ? new THREE.MeshBasicMaterial({ color: ecol })
          : new THREE.MeshStandardMaterial({
              color: ecol,
              emissive: ecol,
              emissiveIntensity: active ? 0.35 : 0.1,
              roughness: 0.4,
              transparent: !active,
              opacity: active ? 1 : go + 0.25,
            });
        const col = new THREE.Mesh(endGeo, endMat);
        col.position.set(wv.x, f.yBase + 0.03, wv.z);
        if (active) (col as any).userData.end = e;
        gEnd.add(col);

        const eabbr = (App.Routing as any).systemAbbrOf(e.systemId);
        const lab = makeLabel(e.id + (eabbr ? '(' + eabbr + ')' : ''), '#cdefff');
        lab.position.set(wv.x, f.yBase + (isPlan ? 0.6 : 2.1), wv.z);
        gMark.add(lab);
      });
    });
  }

  /**
   * 构建竖井贯通立柱
   */
  function buildRisers(): void {
    gRiser.clear();
    const C = App.Config;
    const S = App.Store;
    const W = App.World;

    if (S.planView) return;

    const cols: ShaftColumn[] = W.shaftColumns();
    if (!cols.length) return;

    for (const col of cols) {
      // 竖井按所属系统着色
      const scol = (App.Routing as any).systemColorOf(col.systemId, C.SHAFT_COLOR);
      const sabbr = (App.Routing as any).systemAbbrOf(col.systemId);
      const h = col.yMax - col.yMin + 1.6;

      const geo = new THREE.CylinderGeometry(0.42, 0.42, h, 22, 1, true);
      const m = new THREE.MeshStandardMaterial({
        color: scol,
        transparent: true,
        opacity: 0.26,
        emissive: scol,
        emissiveIntensity: 0.4,
        roughness: 0.3,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geo, m);
      const wv = W.g2wy(col.x, col.z, 0);
      mesh.position.set(wv.x, (col.yMin + col.yMax) / 2, wv.z);
      gRiser.add(mesh);

      const cap = new THREE.Mesh(
        new THREE.CylinderGeometry(0.46, 0.46, 0.12, 22),
        new THREE.MeshStandardMaterial({
          color: scol,
          emissive: scol,
          emissiveIntensity: 0.6,
          roughness: 0.3,
          transparent: true,
          opacity: 0.7,
        })
      );
      cap.position.set(wv.x, col.yMax + 0.78, wv.z);
      gRiser.add(cap);

      const lab = makeLabel('井' + col.id + (sabbr ? '(' + sabbr + ')' : ''), '#cdefff', true);
      lab.position.set(wv.x, col.yMax + 1.15, wv.z);
      gRiser.add(lab);
    }
  }

  /**
   * 构建路径管道
   */
  function buildPath(end: End, path: Node3D[]): THREE.Mesh | null {
    const S = App.Store;
    const W = App.World;

    const pts = path.map(p => {
      const f = S.floors[p.fi];
      const wv = W.g2wy(p.x, p.z, f.yBase);
      return new THREE.Vector3(wv.x, f.yBase + 0.35, wv.z);
    });

    if (pts.length < 2) return null;

    const curve = new THREE.CatmullRomCurve3(pts, false, 'centripetal');
    const geo = new THREE.TubeGeometry(curve, Math.max(24, pts.length * 4), 0.16, 10, false);
    // 路径按末端所属管线系统着色
    const pcol = (App.Routing as any).systemColorOf(end.systemId, end.color);
    const mat = new THREE.MeshStandardMaterial({
      color: pcol,
      emissive: pcol,
      emissiveIntensity: 0.4,
      roughness: 0.35,
      metalness: 0.2,
      transparent: true,
      opacity: 0.95,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.scale.setScalar(0.01);
    gPath.add(mesh);

    // 流向动画光点：沿同一曲线排开 N 个光点，loop 中推进参数 t (0→1 = 机房→末端)
    const N = 6;
    const flowPos = new Float32Array(N * 3);
    const flowGeo = new THREE.BufferGeometry();
    flowGeo.setAttribute('position', new THREE.BufferAttribute(flowPos, 3));
    const flowMat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.42,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      sizeAttenuation: true,
    });
    const flowPts = new THREE.Points(flowGeo, flowMat);
    flowPts.visible = S.flowAnim !== false;
    gPath.add(flowPts);
    pathFlows.push({ curve, points: flowPts, color: pcol });

    return mesh;
  }

  /**
   * 构建搜索过程可视化
   */
  function buildSearch(visited: Node3D[]): void {
    gSearch.clear();
    const S = App.Store;
    const W = App.World;

    if (!visited.length) return;

    const byF = new Map<number, Node3D[]>();
    for (const v of visited) {
      if (!byF.has(v.fi)) byF.set(v.fi, []);
      byF.get(v.fi)!.push(v);
    }

    const geo = new THREE.BoxGeometry(0.86, 0.06, 0.86);

    for (const [fi, vs] of byF) {
      const f = S.floors[fi];
      if (!f) continue;

      const mesh = new THREE.InstancedMesh(
        geo,
        new THREE.MeshBasicMaterial({ color: 0x3da9fc, transparent: true, opacity: 0.35, depthWrite: false }),
        vs.length
      );
      const mx = new THREE.Matrix4();

      vs.forEach((p, i) => {
        const wv = W.g2wy(p.x, p.z, f.yBase);
        mx.makeTranslation(wv.x, f.yBase + 0.18, wv.z);
        mesh.setMatrixAt(i, mx);
      });
      mesh.instanceMatrix.needsUpdate = true;
      (mesh as any).frustumCulled = false;
      gSearch.add(mesh);
    }
  }

  /**
   * 构建交接点
   */
  function buildJunctions(): void {
    gJunction.clear();
    const W = App.World;
    const S = App.Store;

    for (const f of S.floors) {
      if (S.planView && f !== S.f) continue;
      if (!f.junctions.length) continue;

      const geo = new THREE.SphereGeometry(0.22, 16, 16);
      const m = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0x9fd2ff,
        emissiveIntensity: 0.95,
        roughness: 0.3,
      });
      const mesh = new THREE.InstancedMesh(geo, m, f.junctions.length);
      const mx = new THREE.Matrix4();

      f.junctions.forEach((p, i) => {
        const wv = W.g2wy(p.x, p.z, f.yBase);
        mx.makeTranslation(wv.x, f.yBase + 0.55, wv.z);
        mesh.setMatrixAt(i, mx);
      });
      mesh.instanceMatrix.needsUpdate = true;
      (mesh as any).frustumCulled = false;
      gJunction.add(mesh);
    }
  }

  // 预览用持久几何/材质
  const prevCellGeo = new THREE.BoxGeometry(0.92, 0.14, 0.92);
  const prevCellMat = new THREE.MeshStandardMaterial({
    color: 0x3df0ff,
    transparent: true,
    opacity: 0.5,
    emissive: 0x3df0ff,
    emissiveIntensity: 0.45,
    depthWrite: false,
  });
  const vGeo = new THREE.SphereGeometry(0.17, 12, 12);
  const vMat = new THREE.MeshStandardMaterial({
    color: 0x3df0ff,
    emissive: 0x3df0ff,
    emissiveIntensity: 0.6,
  });
  const hoverMesh = new THREE.Mesh(
    new THREE.BoxGeometry(0.98, 0.04, 0.98),
    new THREE.MeshBasicMaterial({ color: 0x3df0ff, transparent: true, opacity: 0.4, depthWrite: false })
  );
  hoverMesh.visible = false;
  gHover.add(hoverMesh);

  /**
   * 构建连线预览
   */
  function buildPreview(): void {
    const W = App.World;
    const S = App.Store;

    gPreview.clear();

    if (S.mode !== 'line') {
      hoverMesh.visible = false;
      return;
    }

    const yb = S.f.yBase;
    const verts = S.curLine.slice();
    if (S.hoverCell) verts.push(S.hoverCell);

    const cells = verts.length >= 2 ? W.rasterizeVertices(verts) : [];

    if (cells.length) {
      const mesh = new THREE.InstancedMesh(prevCellGeo, prevCellMat, cells.length);
      const mx = new THREE.Matrix4();

      cells.forEach((p, i) => {
        const wv = W.g2wy(p.x, p.z, yb);
        mx.makeTranslation(wv.x, yb + 0.12, wv.z);
        mesh.setMatrixAt(i, mx);
      });
      mesh.instanceMatrix.needsUpdate = true;
      gPreview.add(mesh);
    }

    for (const v of S.curLine) {
      const wv = W.g2wy(v.x, v.z, yb);
      const s = new THREE.Mesh(vGeo, vMat);
      s.position.set(wv.x, yb + 0.4, wv.z);
      gPreview.add(s);
    }

    if (S.hoverCell) {
      const wv = W.g2wy(S.hoverCell.x, S.hoverCell.z, yb);
      hoverMesh.position.set(wv.x, yb + 0.3, wv.z);
      hoverMesh.visible = true;
    } else {
      hoverMesh.visible = false;
    }
  }

  /**
   * 路径生长动画
   */
  function animateGrow(mesh: THREE.Mesh | null, t0: number, dur: number): void {
    if (!mesh) return;
    const tick = () => {
      const k = Math.min(1, (performance.now() - t0) / dur);
      mesh.scale.setScalar(0.3 + 0.7 * (1 - Math.pow(1 - k, 3)));
      if (k < 1) requestAnimationFrame(tick);
      else mesh.scale.setScalar(1);
    };
    tick();
  }

  return {
    canvas,
    renderer,
    scene,
    camera,
    controls,
    cameraOrtho,
    controlsOrtho,
    gGrid,
    gWalk,
    gForbid,
    gRoom,
    gEnd,
    gMark,
    gPath,
    gSearch,
    gJunction,
    gRiser,
    gRiserTile,
    gPreview,
    gHover,
    gSel,
    hoverMesh,

    makeLabel,
    fitCamera,
    setupOrtho,
    buildGrid,
    buildTiles,
    buildRisers,
    buildPath,
    buildSearch,
    buildJunctions,
    buildPreview,
    animateGrow,

    rebuildAll(): void {
      buildGrid();
      buildTiles();
      buildJunctions();
      buildRisers();
      buildPreview();
    },

    activeCamera(): THREE.Camera {
      return App.Store.planView ? cameraOrtho : camera;
    },

    activeControls(): _OrbitControls {
      return App.Store.planView ? controlsOrtho : controls;
    },

    setPlanView(on: boolean): void {
      const S = App.Store;
      S.planView = on;
      const isLight = S.theme === 'light';

      if (on) {
        // 平面视图：关闭所有光照
        allLights.forEach(l => l.visible = false);
        scene.fog = null;
        scene.background = new THREE.Color(isLight ? 0xe8ecf2 : 0x1a1a2e);
        controls.enabled = false;
        controlsOrtho.enabled = true;
        this.focusPlan();
      } else {
        // 3D 视图：恢复光照
        allLights.forEach(l => l.visible = true);
        controlsOrtho.enabled = false;
        controls.enabled = true;
        const bg3D = isLight ? 0xf0f2f5 : 0x0b1020;
        sceneFog = new THREE.Fog(bg3D, 60, 150);
        scene.fog = sceneFog;
        scene.background = new THREE.Color(bg3D);
        this.fitCamera();
      }

      this.rebuildAll();
      App.UI.renderFloorTabs();
    },

    focusPlan(): void {
      const S = App.Store;
      const yb = S.f ? S.f.yBase : 0;
      cameraOrtho.position.set(0, yb + 30, 0);
      cameraOrtho.zoom = 1;
      cameraOrtho.up.set(0, 0, -1);
      controlsOrtho.target.set(0, yb, 0);
      setupOrtho();
      controlsOrtho.update();
    },

    loop(): void {
      const S = App.Store;
      if (S.planView) controlsOrtho.update();
      else controls.update();

      // 流向动画：光点沿曲线从机房端(t=0)流向末端端(t=1)
      const on = S.flowAnim !== false;
      if (pathFlows.length) {
        const now = performance.now();
        for (const f of pathFlows) {
          f.points.visible = on;
          if (!on) continue;
          const attr = f.points.geometry.getAttribute('position') as THREE.BufferAttribute;
          const arr = attr.array as Float32Array;
          const n = arr.length / 3;
          for (let i = 0; i < n; i++) {
            const t = (now * 0.00035 + i / n) % 1;
            const p = f.curve.getPointAt(t);
            arr[i * 3] = p.x;
            arr[i * 3 + 1] = p.y;
            arr[i * 3 + 2] = p.z;
          }
          attr.needsUpdate = true;
        }
      }

      renderer.render(scene, this.activeCamera());
    },

    /** 清空流向动画光点（在清除路径时调用） */
    clearFlows(): void {
      pathFlows.length = 0;
    },

    /** 根据主题更新 Three.js 场景背景色和雾色 */
    setSceneTheme(_theme: string): void {
      const S = App.Store;
      const bg3D = S.theme === 'light' ? 0xf0f2f5 : 0x0b1020;
      // 更新全局 fog 引用（3D 视图用）
      sceneFog = new THREE.Fog(bg3D, 60, 150);
      if (!S.planView) {
        scene.background = new THREE.Color(bg3D);
        scene.fog = sceneFog;
      } else {
        scene.background = new THREE.Color(S.theme === 'light' ? 0xe8ecf2 : 0x1a1a2e);
      }
      this.rebuildAll();
    },
  };
})();

export default App.Render;
export const Render = App.Render;
